<!-- Custom Toast Component -->
<app-custom-toast />

<!-- Global Confirmation Dialog -->
<app-confirmation-dialog 
  [isVisible]="confirmationDialogService.visible()"
  [heading]="confirmationDialogService.config()?.title || 'Confirmation'"
  [subtitle]="confirmationDialogService.config()?.message || ''"
  [confirmLabel]="confirmationDialogService.config()?.confirmLabel || 'Yes'"
  [cancelLabel]="confirmationDialogService.config()?.cancelLabel || 'No'"
  (confirmed)="confirmationDialogService.confirm()"
  (cancelled)="confirmationDialogService.cancel()"
/>

<!-- Global Plan Dialog -->
@if (planDialogService.isVisible()) {
  <app-client-plan
    [clientId]="planDialogService.clientId()"
    [planId]="planDialogService.planId()"
    [activeJobId]="planDialogService.activeJobId()"
    [trainingType]="planDialogService.trainingType()"
    [branchId]="planDialogService.branchId()"
    (close)="onPlanDialogClose()"
    (planSaved)="onPlanSaved()"
    (hiddenInBackground)="onPlanDialogClose()">
  </app-client-plan>
}

  @if (isFullScreenRoute) {
  <div class="relative w-full h-screen">
    @if (isRouteLoading) {
      <div class="route-loading-overlay absolute inset-0 z-[var(--z-loading)] flex items-center justify-center" 
           role="status" 
           aria-live="polite" 
           aria-label="Loading page">
        <app-global-loading 
          [loadingText]="'Loading page...'" 
          [isLoading]="isRouteLoading"
        />
      </div>
    }
    <router-outlet />
  </div>
  } @else {
  <!-- Mobile/Tablet Layout -->
  <div class="block h-screen md:h-screen flex flex-col w-full p-0 md:px-6 md:pb-6 relative overflow-hidden md:overflow-visible bg-white/70  md:bg-transparent"
       style="padding-top: env(safe-area-inset-top);">
    <!-- Top Header - Hidden on mobile, visible on desktop -->
    <div class="hidden md:flex bg-transparent h-10 mb-4.5 rounded-3xl items-center justify-between w-full">
      <img src="vixey-logo.png" alt="Vixey" class="select-none h-10 mt-2" />
      <app-navbar (subNavChange)="onSubNavChange($event)" (activeSubNavChange)="onActiveSubNavChange($event)" />
      <div class="flex items-center gap-3">
        <!-- Workout Generation Jobs Indicator -->
        @if (permissionService.canViewClientWorkoutPlans()) {
          <div class="relative">
            <button
              (click)="openWorkoutJobsOverlay($event)"
              class="relative flex items-center justify-center w-8 h-8 mt-2 rounded-[1.625rem] border border-white bg-white/50 shadow-[0_2px_10.4px_0_rgba(0,0,0,0.08)] text-grey-950 cursor-pointer transition-all duration-300 ease-in-out hover:scale-105 active:scale-95"
              pTooltip="Workout Generation"
              tooltipPosition="bottom"
              tooltipStyleClass="small-tooltip">
              <span class="pi pi-bolt text-lg"></span>
              @if (workoutJobsStateService.activeJobsCount() > 0) {
                <span class="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary-600 text-white text-[10px] font-semibold leading-none z-[var(--z-header-components)]">
                  {{ workoutJobsStateService.activeJobsCount() > 99 ? '99+' : workoutJobsStateService.activeJobsCount() }}
                </span>
              }
            </button>
          </div>
        }

        <!-- Profile Avatar -->
        <button 
          (click)="openProfileMenuOverlay($event)"
          class="flex items-center justify-center w-8 h-8 mt-2 rounded-full overflow-hidden transition-all duration-300 ease-in-out hover:scale-105 active:scale-95 border-2 border-white shadow-sm [-webkit-tap-highlight-color:transparent]">
          @if (profileService.profileDetails()?.profileImagePath) {
            <img [src]="profileService.profileDetails()?.profileImagePath" 
                 alt="Profile" 
                 class="w-full h-full object-cover" />
          } @else {
            <div class="w-full h-full rounded-full bg-black flex items-center justify-center">
              <span class="text-white text-sm font-semibold">
                {{ fullName() | initials }}
              </span>
            </div>
          }
        </button>
      </div>
    </div>

    <!-- Workout Jobs Popover -->
    @if (permissionService.canViewClientWorkoutPlans() ) {
      <app-workout-jobs-popover #workoutJobsPopover
        (jobClick)="onJobClick($event)"
        (cancelJob)="onCancelJob($event)"
        (retryJob)="onRetryJob($event)">
      </app-workout-jobs-popover>
    }

    <!-- Profile Menu Popover -->
    <app-profile-menu-popover #profileMenuPopover
      (logoutClick)="logout()"
      [isLoggingOut]="logoutService.isLoggingOut()">
    </app-profile-menu-popover>

    <div class="w-full subnav-container hidden md:block" [ngClass]="{ show: subNavItems.length > 0 }"
      [style.margin-bottom]="subNavItems.length > 0 ? '0px' : '-4px'">
      <foxera-subnav [subNavItems]="subNavItems" [activeSubItem]="activeSubNavItem"
        (activeItemIndexChange)="onActiveSubNavIndexChange($event)" />
    </div>

    <div class="flex flex-col w-full flex-1 overflow-y-auto body-container relative 
                rounded-none md:rounded-3xl bg-transparent md:bg-white/60 
                p-0 md:px-10 md:py-5" 
         [ngClass]="{
           'md:px-10 md:py-5': !hasReducedPadding,
           'md:px-0 md:py-0': hasReducedPadding,
           'md:rounded-3xl': subNavItems.length === 0,
           'md:rounded-b-3xl md:rounded-tr-3xl': subNavItems.length > 0,
           'md:rounded-tl-none': subNavItems.length > 0 && activeSubNavIndex === 0,
           'md:rounded-tl-3xl': subNavItems.length > 0 && activeSubNavIndex !== 0
         }">
      @if (isRouteLoading) {
        <div class="route-loading-overlay absolute inset-x-0 top-0 bottom-0 md:inset-0 z-[var(--z-loading)] flex items-center justify-center rounded-[inherit]" 
             role="status" 
             aria-live="polite" 
             aria-label="Loading page">
          <app-global-loading 
            [loadingText]="'Loading page...'" 
            [isLoading]="isRouteLoading"
          />
        </div>
      }
      <router-outlet />
    </div>

    <!-- Mobile Bottom Navigation -->
    <div class="bg-white/50 border-t-2 border-white   
                px-3 pt-2 md:hidden shadow-[0_-2px_12px_rgba(0,0,0,0.06)]"
         style="padding-bottom: calc(0.5rem + env(safe-area-inset-bottom));">
      <app-navbar (subNavChange)="onSubNavChange($event)" (activeSubNavChange)="onActiveSubNavChange($event)" />
    </div>
  </div>
  }